import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Arrow } from 'app/components/Common/Arrow'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { Media } from 'app/theme/media'
import useMediaQuery from 'app/utils/useMediaQuery'
import { rgba } from 'emotion-rgba'
import { useKeenSlider } from 'keen-slider/react'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Parallax } from 'react-scroll-parallax'

import { Vimeo } from './Vimeo'

const AUTOPLAY_TIMER = 5000

export interface ClaimProps {
  claim?: string
  subtitle?: string
}

export interface Props {
  claims?: ClaimProps[]
  idVideo?: string
  idVideoMobile?: string
  image?: ImageProps
  images?: ImageProps[]
  mobileImage?: ImageProps
  mobileImages?: ImageProps[]
  siteName?: string
  variant?: Variant
}

export const AdvancedHero = memo(function AdvancedHero({
  claims,
  idVideo,
  idVideoMobile,
  images,
  mobileImages,
  siteName,
  variant = 'default',
}: Props) {
  if (!images) {
    return null
  }
  if (!claims) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const [animObjectRef, isAnimObjectVisible] = useInView({ triggerOnce: true })

  const isMobile = useMediaQuery('(max-width: 1199px)')
  const [sliderImages, setSliderImages] = useState<any>(
    isMobile ? mobileImages : images,
  )

  const [currentSlide, setCurrentSlide] = useState(0)
  const [opacities, setOpacities] = useState<number[]>([])
  const [details, setDetails] = useState<any>(null)
  const [loaded, setLoaded] = useState<boolean[]>([])
  const [nextLoaded, setNextLoaded] = useState<boolean[]>([])
  const timer = useRef<any>()

  function positionStyle(idx: any) {
    if (!details) {
      return undefined
    }

    const winWidth = window.innerWidth

    const position = details.slides[idx].distance
    const x = (instanceRef.current?.size || winWidth) * -position
    return {
      transform: `translateX(${x + position * (winWidth / 2)}px)`,
      WebkitTransform: `translateX(${x + position * (winWidth / 2)}px)`,
    }
  }

  const [sliderRef, instanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1500,
    },
    loop: images.length > 1 ? true : false,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
      const newOpacities = s.track.details.slides.map((slide) => slide.portion)

      claims.length > 1 ? setDetails(s.track.details) : null
      claims.length > 1 ? setOpacities(newOpacities) : null
    },
  })

  const [claimsSliderRef, claimsInstanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1500,
    },
    drag: false,
    loop: images.length > 1 ? true : false,
  })

  useEffect(() => {
    isMobile ? setSliderImages(mobileImages) : setSliderImages(images)

    const newLoaded = [...loaded]
    newLoaded[currentSlide] = true
    const newNextLoaded = [...nextLoaded]
    newNextLoaded[currentSlide + 1] = true

    setLoaded(newLoaded)
    setNextLoaded(newNextLoaded)

    timer.current = setInterval(() => {
      if (instanceRef) {
        instanceRef.current?.next()
      }
    }, AUTOPLAY_TIMER)

    return () => {
      clearInterval(timer.current)
    }
  }, [
    currentSlide,
    instanceRef,
    claimsInstanceRef,
    claimsSliderRef,
    sliderRef,
    sliderImages,
    isMobile,
  ])

  return (
    <Container variant={variant}>
      {idVideo || idVideoMobile ? (
        <>
          <Media lessThan="ipadVertical">
            {idVideoMobile ? (
              <Vimeo placeholder={sliderImages[0]} ID={idVideoMobile} />
            ) : null}
          </Media>

          <Media greaterThanOrEqual="ipadVertical">
            {idVideo ? (
              <Vimeo placeholder={sliderImages[0]} ID={idVideo} />
            ) : null}
          </Media>
        </>
      ) : (
        <>
          <Parallax className="wrapper" translateY={['-20%', '20%']}>
            <Slider ref={sliderRef}>
              {sliderImages.map((item: any, index: number) => (
                <Slide key={index} className="keen-slider__slide">
                  <Inner style={positionStyle(index)}>
                    <Image
                      alt={loaded[index] || nextLoaded[index] ? item.alt : ''}
                      {...(loaded[index] || nextLoaded[index] ? item : null)}
                    />
                  </Inner>
                </Slide>
              ))}
            </Slider>
          </Parallax>
        </>
      )}

      {variant !== 'compact' ? (
        <>
          <Wrapper variant={variant}>
            <Parallax translateY={['20%', '-30%']}>
              <Claims ref={claimsSliderRef}>
                {claims.map((item: any, index) => (
                  <Item
                    style={{
                      opacity: opacities[index],
                    }}
                    key={index}
                    className="keen-slider__slide"
                  >
                    {siteName ? (
                      <FadeInUp delay={0.6}>
                        <SiteName>{siteName}</SiteName>
                      </FadeInUp>
                    ) : null}

                    <Claim tag="h2" length={30} text={item.claim} />

                    <Subtitle tag="h3" length={30} text={item.subtitle} />
                  </Item>
                ))}
              </Claims>
            </Parallax>

            {images.length > 1 ? (
              <FadeInUp delay={1}>
                <Arrows dial={4} row>
                  <Arrow
                    variant="hero"
                    onClick={(e) =>
                      e.stopPropagation() || instanceRef.current?.prev()
                    }
                  />

                  <Arrow
                    variant="hero"
                    direction="R"
                    onClick={(e) =>
                      e.stopPropagation() || instanceRef.current?.next()
                    }
                  />
                </Arrows>
              </FadeInUp>
            ) : null}
          </Wrapper>
          <CounterWrapper
            ref={animObjectRef}
            className={`${isAnimObjectVisible ? ' visible' : ''}`}
            dial={4}
            row
          >
            {sliderImages.map((item: any, index: number) => (
              <Counter
                key={index}
                className={`${currentSlide === index ? 'active' : ''}`}
                onClick={() => {
                  instanceRef.current?.moveToIdx(index)
                }}
              >
                {`0${index + 1}`}
              </Counter>
            ))}
          </CounterWrapper>
        </>
      ) : null}
    </Container>
  )
})

const Slider = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  outline: none;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
`

const Slide = styled.div`
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const Container = styled.section<ContainerProps>`
  height: 100vh;
  max-height: -webkit-fill-available;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.variants.neutralDark3};
  overflow: hidden;
  position: relative;
  &:before,
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
  }
  &:before {
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0.6)},
      ${rgba(theme.colors.variants.neutralDark3, 0)},
      ${rgba(theme.colors.variants.neutralDark3, 0.6)}
    );
  }
  &:after {
    background: ${({ theme }) => theme.colors.variants.neutralDark3};
    opacity: 0.1;
  }

  .parallax {
    height: 100%;
  }

  .wrapper {
    height: 100%;
    position: relative;
    z-index: 2;
  }

  img {
    ${styles.img}
  }

  ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return css`
          height: 60vh;

          .wrapper {
            transform: none !important;
          }

          @media (max-width: 1199px) {
            height: 51.502vh;
          }
        `
    }
  }}
`

const Claims = styled.div`
  width: 100%;
  height: 100%;
  min-height: 13.125rem;
  outline: none;
  overflow: hidden;
  position: relative;
  left: 0;

  @media (max-width: 1199px) {
    text-align: center;
    min-height: 9rem;
  }
`

const Item = styled.div`
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 0;
`

const Claim = styled(AnimatedTitle)`
  ${styles.title.xl}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
  line-height: 4.125rem;

  @media (max-width: 1199px) {
    font-size: 3rem;
    line-height: 3rem;
  }
  @media (max-width: 574px) {
    font-size: 2.25rem;
    line-height: 2.5rem;
  }
`

const Subtitle = styled(AnimatedTitle)`
  ${styles.subtitle}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
  display: block;
  margin-top: 0.5rem;
  font-size: 1rem;
  line-height: 1.25rem;
  text-transform: uppercase;
`

const Wrapper = styled.div<ContainerProps>`
  position: absolute;
  bottom: 6rem;
  left: 10vw;
  right: 10vw;
  z-index: 4;

  @media (max-width: 1199px) {
    left: 1.5625rem;
    right: 1.5625rem;
    bottom: 9rem;
  }
`

const SiteName = styled.div`
  ${styles.description.light}

  margin-bottom: 3rem;

  @media (max-width: 1199px) {
    margin-bottom: 1.5rem;
  }
`

const CounterWrapper = styled(FlexBox)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 4rem;
  z-index: 4;
  opacity: 0;

  transition: 0.4s 1s ease-in-out;

  &.visible {
    opacity: 1;
    bottom: 6rem;
  }
`

const Counter = styled.div`
  ${styles.label}

  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1rem;
  opacity: 0.3;
  transition: opacity 0.4s ease-in-out;

  &:not(:last-of-type) {
    margin-right: 1.5rem;
  }

  &:hover,
  &.active {
    opacity: 1;
  }
`

const Arrows = styled(FlexBox)`
  > div {
    width: 2.25rem;
    height: 0.5625rem;

    svg {
      height: auto;
      width: 2.25rem;
    }

    &:first-of-type {
      margin-right: 1.5rem;
    }
  }
  @media (max-width: 1199px) {
    justify-content: center;
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'compact' | 'home'
